import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Trash2 } from 'lucide-react';
import api from '@/lib/api';
import { AdminLoadError } from '../components/AdminLoadError';
import { UserIdentity } from '../components/UserIdentity';
import { StatusFilterCheckboxes } from '../components/StatusFilterCheckboxes';
import { SupportRefId, AwaitingReplyFlag, ReplyWaivedCheckbox } from '../components';
import { useReplyWaiver } from '../hooks/useReplyWaiver';
import { useStatusFilterParam } from '../hooks/useStatusFilterParam';
import { useSessionPersistedSearchParams } from '../hooks/useSessionPersistedSearchParams';
import { useSupportUnreadCounts } from '../hooks/useSupportUnreadCounts';
import type { SupportUserRef } from '@/lib/api';

interface FiasAiReport {
  reportId: string;
  // Null for a temporary chat, or once the conversation was deleted — the
  // report itself survives as a self-contained diagnostic record.
  conversationId: string | null;
  userId: string;
  user?: SupportUserRef | null;
  unread?: boolean;
  awaitingReply?: boolean;
  replyWaived?: boolean;
  description: string;
  modelInfo: unknown | null;
  userAgent: string | null;
  pageUrl: string | null;
  status: string;
  adminNotes: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  {
    value: 'investigating',
    label: 'Investigating',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  { value: 'closed', label: 'Closed', color: 'bg-gray-100 text-gray-800 border-gray-200' },
];

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return (
    <Badge variant="outline" className={config.color}>
      {config.label}
    </Badge>
  );
}

/** Best-effort human label for the captured model snapshot ({@link currentModel}). */
function modelLabel(modelInfo: unknown): string {
  if (!modelInfo || typeof modelInfo !== 'object') return '—';
  const m = modelInfo as { displayName?: string; modelName?: string; tier?: string };
  return m.displayName || m.modelName || m.tier || '—';
}

function summarizeDescription(text: string): string {
  const firstLine = text.split('\n')[0];
  return firstLine.length > 80 ? `${firstLine.slice(0, 77)}…` : firstLine;
}

// Remember the status filter for the session, so it survives opening a report
// and returning via the bare-route back link.
const VIEW_STORAGE_KEY = 'support-admin-fias-ai-reports-view';
const VIEW_PARAM_KEYS = ['status'] as const;

export function AdminFiasAiReportsPage() {
  const [reports, setReports] = useState<FiasAiReport[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { statuses, setStatuses } = useStatusFilterParam();
  // Side-effect only: persist/restore the status filter (written via the hook).
  useSessionPersistedSearchParams(VIEW_STORAGE_KEY, VIEW_PARAM_KEYS);
  const { refresh: refreshUnread } = useSupportUnreadCounts();

  // Edit dialog
  const [editReport, setEditReport] = useState<FiasAiReport | null>(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const replyWaiver = useReplyWaiver('fias-ai-reports');
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteReport, setDeleteReport] = useState<FiasAiReport | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await api.listFiasAiReportsAdmin({
        status: statuses.length ? statuses : undefined,
        limit: 50,
      });
      setReports(result.reports);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to load Fias AI reports:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [statuses]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleEdit = (report: FiasAiReport) => {
    setEditReport(report);
    setEditStatus(report.status);
    setEditNotes(report.adminNotes || '');
    replyWaiver.reset(report.replyWaived);
    if (report.unread) {
      setReports((prev) =>
        prev.map((r) => (r.reportId === report.reportId ? { ...r, unread: false } : r)),
      );
      api
        .markFiasAiReportReadAdmin(report.reportId)
        .then(() => refreshUnread())
        .catch(() => {});
    }
  };

  const handleSave = async () => {
    if (!editReport) return;
    setSaving(true);
    try {
      // Waiver first: if it fails, nothing has been committed yet.
      await replyWaiver.save(editReport.reportId);
      await api.updateFiasAiReportAdmin(editReport.reportId, {
        status: editStatus,
        adminNotes: editNotes.trim() || null,
      });
      setEditReport(null);
      await fetchReports();
    } catch (err) {
      console.error('Failed to update Fias AI report:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteReport) return;
    setDeleting(true);
    try {
      await api.deleteFiasAiReportAdmin(deleteReport.reportId);
      setDeleteReport(null);
      await fetchReports();
    } catch (err) {
      console.error('Failed to delete Fias AI report:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Fias AI Reports</h2>
          <p className="text-sm text-muted-foreground">
            {total} report{total !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Status filter (multi-select) */}
      <StatusFilterCheckboxes options={STATUS_OPTIONS} selected={statuses} onChange={setStatuses} />

      {/* Table */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : error ? (
        <AdminLoadError what="Fias AI reports" onRetry={fetchReports} />
      ) : reports.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No Fias AI reports found.</p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[36px]">
                  <span className="sr-only">Awaiting reply</span>
                </TableHead>
                <TableHead>Summary</TableHead>
                <TableHead className="w-[160px]">Report ID</TableHead>
                <TableHead className="w-[160px]">Customer</TableHead>
                <TableHead className="w-[140px]">Model</TableHead>
                <TableHead className="w-[130px]">Status</TableHead>
                <TableHead className="w-[100px]">Date</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports.map((r) => (
                <TableRow key={r.reportId} className="cursor-pointer" onClick={() => handleEdit(r)}>
                  <TableCell>
                    <AwaitingReplyFlag awaiting={r.awaitingReply} />
                  </TableCell>
                  <TableCell>
                    <p className="flex items-center gap-1.5 text-sm truncate max-w-[400px]">
                      {r.unread && (
                        <span
                          className="h-2 w-2 flex-shrink-0 rounded-full bg-primary"
                          title="Unread"
                        />
                      )}
                      <span className={r.unread ? 'font-semibold' : 'font-medium'}>
                        {summarizeDescription(r.description)}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground truncate max-w-[400px]">
                      conversation {r.conversationId ?? 'temporary / deleted'}
                    </p>
                  </TableCell>
                  <TableCell className="max-w-[160px]">
                    <SupportRefId id={r.reportId} label="Report ID" />
                  </TableCell>
                  <TableCell className="max-w-[160px]">
                    <UserIdentity user={r.user} userId={r.userId} />
                  </TableCell>
                  <TableCell className="text-xs">{modelLabel(r.modelInfo)}</TableCell>
                  <TableCell>
                    <StatusBadge status={r.status} />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(r.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteReport(r);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editReport} onOpenChange={(open) => !open && setEditReport(null)}>
        <DialogContent className="max-w-2xl">
          {editReport && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  Fias AI Report
                  <StatusBadge status={editReport.status} />
                </DialogTitle>
                <SupportRefId
                  id={editReport.reportId}
                  label="Report ID"
                  variant="full"
                  className="-ml-1 self-start"
                />
                <DialogDescription>
                  Reported {new Date(editReport.createdAt).toLocaleString()} by{' '}
                  <span title={editReport.userId}>
                    {editReport.user?.displayName ?? editReport.user?.username ?? editReport.userId}
                  </span>
                </DialogDescription>
              </DialogHeader>

              <div className="rounded-md bg-muted p-3 max-h-[260px] overflow-y-auto">
                <p className="text-xs font-medium text-muted-foreground mb-1">Description:</p>
                <p className="text-sm whitespace-pre-wrap">{editReport.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="rounded-md bg-muted/50 p-2">
                  <p className="font-medium text-muted-foreground">Conversation</p>
                  <p className="font-mono truncate">
                    {editReport.conversationId ?? 'temporary / deleted'}
                  </p>
                </div>
                <div className="rounded-md bg-muted/50 p-2">
                  <p className="font-medium text-muted-foreground">Model</p>
                  <p className="truncate">{modelLabel(editReport.modelInfo)}</p>
                </div>
                {editReport.pageUrl && (
                  <div className="rounded-md bg-muted/50 p-2 col-span-2">
                    <p className="font-medium text-muted-foreground">Page URL</p>
                    <p className="truncate">{editReport.pageUrl}</p>
                  </div>
                )}
                {editReport.reviewedAt && (
                  <div className="rounded-md bg-muted/50 p-2 col-span-2">
                    <p className="font-medium text-muted-foreground">First reviewed</p>
                    <p>
                      {new Date(editReport.reviewedAt).toLocaleString()}{' '}
                      {editReport.reviewedBy ? `by ${editReport.reviewedBy}` : ''}
                    </p>
                  </div>
                )}
              </div>

              {editReport.adminNotes && (
                <div className="rounded-md bg-muted/50 p-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    Previous admin notes:
                  </p>
                  <p className="text-sm whitespace-pre-wrap">{editReport.adminNotes}</p>
                </div>
              )}

              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Status</label>
                  <Select value={editStatus} onValueChange={setEditStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Admin Notes</label>
                  <Textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Internal notes about this report..."
                    rows={3}
                    maxLength={5000}
                  />
                </div>
              </div>

              <ReplyWaivedCheckbox
                waived={replyWaiver.waived}
                onChange={replyWaiver.setWaived}
                disabled={saving}
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditReport(null)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteReport} onOpenChange={(open) => !open && setDeleteReport(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Fias AI Report</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this report? This cannot be undone and the captured
              conversation payload will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteReport(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
